import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Copy, Check } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
      if (data.reset_token) setToken(data.reset_token);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-[420px]">
        <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-8">
          <ArrowLeft className="h-3 w-3" /> Back to sign in
        </Link>

        <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-[0.25em] mb-3">
          Account recovery · /auth/forgot-password
        </div>
        <h2 className="font-display font-extrabold tracking-tight text-3xl">
          Reset your password
        </h2>
        <p className="text-sm text-muted-foreground mt-2 mb-6">
          Enter the email associated with your account. We&apos;ll generate a
          one-time reset token (email delivery ships in Sprint 3).
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">
              Work Email
            </Label>
            <Input
              id="email"
              type="email"
              required
              placeholder="you@institution.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email-input"
              className="h-11"
            />
          </div>
          {error && (
            <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11"
            data-testid="forgot-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset token"}
          </Button>
        </form>

        {msg && (
          <div className="mt-6 border border-border rounded-md p-4 bg-secondary/50" data-testid="forgot-success">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Status
            </div>
            <div className="text-sm">{msg}</div>
            {token && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                  Reset Token (dev preview)
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-xs bg-background border border-border rounded px-2 py-1.5 truncate" data-testid="reset-token-value">
                    {token}
                  </code>
                  <Button size="sm" variant="outline" onClick={copy} type="button" data-testid="copy-token-button">
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                <Link
                  to={`/reset-password?token=${encodeURIComponent(token)}`}
                  className="mt-3 inline-block text-xs text-primary font-semibold hover:underline"
                  data-testid="proceed-to-reset"
                >
                  Continue to reset password →
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
